import React, { useState, useRef, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiCheck, FiPlus, FiBriefcase, FiShield } = FiIcons;

/**
 * TenantSwitcher
 * Allows multi-organization switching & active workspace indicator
 */
export const TenantSwitcher = ({ isCollapsed = false }) => {
  const { organizations, activeTenant, switchTenant } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isCollapsed) {
    return (
      <div className="relative flex justify-center py-2" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-sm transition-transform hover:scale-105 active:scale-95"
          style={{ backgroundColor: activeTenant.branding?.primaryColor || '#2563eb' }}
          title={`${activeTenant.name} (${activeTenant.plan})`}
        >
          {activeTenant.name.substring(0, 2).toUpperCase()}
        </button>

        {isOpen && (
          <div className="absolute left-full top-0 ml-3 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95">
            <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
              <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">Organizations</p>
            </div>
            <div className="py-1 space-y-1">
              {organizations.map((org) => {
                const isActive = org.id === activeTenant.id;
                return (
                  <button
                    key={org.id}
                    onClick={() => {
                      switchTenant(org.id);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left flex items-center justify-between p-2 rounded-lg text-xs transition-colors ${
                      isActive 
                        ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-semibold' 
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center text-[10px] text-white font-bold flex-shrink-0"
                        style={{ backgroundColor: org.branding?.primaryColor || '#2563eb' }}
                      >
                        {org.name.substring(0, 1)}
                      </div>
                      <span className="truncate">{org.name}</span>
                    </div>
                    {isActive && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/60 transition-all text-left group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-sm flex-shrink-0"
            style={{ backgroundColor: activeTenant.branding?.primaryColor || '#2563eb' }}
          >
            {activeTenant.name.substring(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-xs font-semibold text-slate-100 truncate group-hover:text-white">
              {activeTenant.name}
            </h4>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <SafeIcon icon={FiShield} className="w-3 h-3 text-emerald-400" />
              <span className="truncate">{activeTenant.plan}</span>
            </div>
          </div>
        </div>
        <SafeIcon icon={FiChevronDown} className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-2 z-50">
          <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
            <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">Switch Organization</p>
          </div>
          <div className="py-1 max-h-56 overflow-y-auto space-y-1">
            {organizations.map((org) => {
              const isActive = org.id === activeTenant.id;
              return (
                <button
                  key={org.id}
                  onClick={() => {
                    switchTenant(org.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left flex items-center justify-between p-2 rounded-lg text-xs transition-colors ${
                    isActive 
                      ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-semibold' 
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <div
                      className="w-6 h-6 rounded flex items-center justify-center text-[10px] text-white font-bold flex-shrink-0"
                      style={{ backgroundColor: org.branding?.primaryColor || '#2563eb' }}
                    >
                      {org.name.substring(0, 1)}
                    </div>
                    <div className="truncate">
                      <p className="truncate font-medium">{org.name}</p>
                      <p className="text-[10px] text-slate-400">{org.plan}</p>
                    </div>
                  </div>
                  {isActive && <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
